import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { updateHourLogEmployee } from "../../lib/api";

/**
 * iter-114: Edit one employee's hourly wage. Admin-only — the parent
 * already gates the pencil-icon trigger, so this dialog assumes the
 * caller is authorised. Returns the updated row via ``onSaved`` so the
 * parent can re-run its aggregate totals without a full refetch.
 */
export default function EditRateDialog({ employee, onClose, onSaved }) {
    const [rate, setRate] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (employee) {
            setRate(employee.hourly_rate != null
                ? String(employee.hourly_rate)
                : "");
        }
    }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!employee) return null;

    const save = async () => {
        // Blank = remove the rate (so the row falls back to "no pay").
        const trimmed = (rate || "").trim();
        const next = trimmed === "" ? null : Number(trimmed);
        if (trimmed !== "" && (!Number.isFinite(next) || next < 0)) {
            toast.error("Rate must be a non-negative number");
            return;
        }
        setSaving(true);
        try {
            await updateHourLogEmployee(employee.id, { hourly_rate: next });
            toast.success(next == null
                ? `Removed hourly wage for ${employee.name}`
                : `${employee.name} set to $${next.toFixed(2)}/h`);
            await onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="bg-[#131A2F] border border-[#2A3655] text-white max-w-sm"
                data-testid="edit-rate-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">
                        Hourly wage — {employee.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        Rate (per hour)
                    </label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                        <Input
                            type="number"
                            min="0"
                            step="0.25"
                            value={rate}
                            onChange={(e) => setRate(e.target.value)}
                            placeholder="0.00"
                            data-testid="edit-rate-input"
                            className="bg-[#0A1128] border-[#2A3655] pl-7"
                            autoFocus
                        />
                    </div>
                    <div className="text-[10px] text-slate-500">
                        Blank to remove. Used to compute total pay on every payroll report.
                    </div>
                </div>
                <DialogFooter>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-xs uppercase font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="btn-save-rate"
                        className="px-4 py-2 text-xs uppercase font-bold rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] hover:bg-[#00A3FF]/30 disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                        Save
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
